import { toast } from "sonner";
/* eslint-disable jsx-a11y/anchor-is-valid */
import * as React from "react";
import { useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import JobsRepository from "../../repositories/JobsRepository";
import {FirmDocumentsFolder} from "../../models/FirmDocumentFolder/FirmDocumentsFolder";
import ShortenedPath from "../common/ShortenedPath";
import {UserRole} from "../../models/User";
import ActionConfirmationModal, {ACTION_CONFIGS} from "../common/ActionConfirmationModal";
import ConfirmMigrationEntitiesExportToCsvModal, {CONFIRM_MIGRATION_ENTITIES_EXPORT_CONFIGS} from "../common/ConfirmMigrationEntitiesExportToCsvModal";
import {Trash2} from "lucide-react";
import {Table, TableHeader, TableBody, TableRow, TableHead, TableCell} from "@/components/ui/table";
import { drawerTableWrapperClassName } from "../common/Table/TableStyles";
import {Button} from "@/components/ui/button";
import {Label} from "@/components/ui/label";
import {Checkbox} from "@/components/ui/checkbox";
import { QUERY_KEYS } from "../../constants/QueryKeys";
import { useRequiresReason } from "../../hooks/useRequiresReason";

interface CreateExportFirmDocumentsFoldersToCsvTableProps {
  organizationId: string;
  newFirmDocumentsFoldersForExport: FirmDocumentsFolder[];
  setNewFirmDocumentsFoldersForExport: React.Dispatch<React.SetStateAction<FirmDocumentsFolder[]>>;
  userRole: UserRole;
  isAssignedUser: boolean;
}

const CreateExportFirmDocumentsFoldersToCsvTable: React.FC<CreateExportFirmDocumentsFoldersToCsvTableProps> = ({
  organizationId,
  newFirmDocumentsFoldersForExport,
  setNewFirmDocumentsFoldersForExport,
  userRole,
  isAssignedUser
}: CreateExportFirmDocumentsFoldersToCsvTableProps) => {
  const requiresReason = useRequiresReason();
  const [confirmDeletionModalOpen, setConfirmDeletionModalOpen] = useState<boolean>(false);
  const [exportFilesData, setExportFilesData] = useState<boolean>(false);
  const [exportReason, setExportReason] = useState<string | undefined>("");
  const allRows = [...newFirmDocumentsFoldersForExport];
  const newIds = new Set(newFirmDocumentsFoldersForExport.map((c) => c.id));
  const jobsRepository = new JobsRepository();
  const queryClient = useQueryClient();

  return (
    <div className="flex flex-col gap-4 pt-4 max-h-[40%] min-h-[40%]">
      <div className="flex flex-col gap-2">
        {requiresReason ? (
          <ActionConfirmationModal actionConfig={ACTION_CONFIGS.EXPORT_FIRM_DOCUMENTS_FOLDER} onConfirm={(reason) => { setConfirmDeletionModalOpen(true); setExportReason(reason); }}>
            <Button disabled={!(newFirmDocumentsFoldersForExport.length > 0)}>Export</Button>
          </ActionConfirmationModal>
        ) : (
          <Button disabled={!(newFirmDocumentsFoldersForExport.length > 0)} onClick={() => setConfirmDeletionModalOpen(true)}>Export</Button>
        )}
        <div className="flex items-center justify-between">
          <Label className="text-sm">Export files data</Label>
          <Checkbox checked={exportFilesData} onCheckedChange={(checked) => setExportFilesData(checked as boolean)} />
        </div>
      </div>
      <div className={drawerTableWrapperClassName}>
        <Table containerClassName="flex-1 min-h-0">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[5%] min-w-[48px] text-center" />
              <TableHead className="w-[75%]">Folder Path</TableHead>
              <TableHead />
            </TableRow>
          </TableHeader>
          <TableBody>
            {allRows.map((deleteFirmDocumentsFolder) => {
              return (
                <TableRow key={deleteFirmDocumentsFolder.id} className={newIds.has(deleteFirmDocumentsFolder.id) ? "bg-primary/10" : ""}>
                  <TableCell className="w-[120px] text-center" />
                  <TableCell>
                    <ShortenedPath folderPath={deleteFirmDocumentsFolder.folderPath} maxLength={50} />
                  </TableCell>
                  <TableCell className="flex items-center justify-end px-1.5 py-3">
                    <Button variant="ghost" size="icon-sm" className="text-destructive" aria-label="Cancel"
                      onClick={() => { setNewFirmDocumentsFoldersForExport((prevRows) => prevRows.filter((entity) => entity.id !== deleteFirmDocumentsFolder.id)); }}>
                      <Trash2 className="size-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
      {confirmDeletionModalOpen ? (
        <ConfirmMigrationEntitiesExportToCsvModal open={confirmDeletionModalOpen} entityKeys={newFirmDocumentsFoldersForExport.map((f) => f.folderPath)} exportFilesData={exportFilesData} config={CONFIRM_MIGRATION_ENTITIES_EXPORT_CONFIGS.FIRM_DOCUMENTS_FOLDERS} onClose={() => { setConfirmDeletionModalOpen(false); }}
          onConfirm={async () => {
            await jobsRepository.startCsvGenerationFromFirmDocumentsFolders(organizationId, newFirmDocumentsFoldersForExport.map((f) => ({ id: f.id, folderPath: f.folderPath })), exportFilesData, exportReason);
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.JOBS] });
            queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.EXPORT_FIRM_DOCUMENTS_FOLDERS, organizationId] });
            setNewFirmDocumentsFoldersForExport([]);
            setExportReason(undefined);
            setConfirmDeletionModalOpen(false);
            toast.success("Export started");
          }}
        />
      ) : (<></>)}
    </div>
  );
};

export default CreateExportFirmDocumentsFoldersToCsvTable;
